import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent, Paper, CircularProgress } from '@mui/material';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { TextField, Button } from '@mui/material';
import { getCostsSummary, getFinance, getStats, getOpenAIBilling, saveOpenAIBalance, saveAnthropicBalance, getAnthropicBilling } from '../api';

const StatCard = ({ title, value, color, icon }) => (
  <Card sx={{ bgcolor: '#1a1a2e', border: '1px solid #333', borderRadius: 2 }}>
    <CardContent>
      <Typography variant="body2" sx={{ color: '#aaa' }}>{icon} {title}</Typography>
      <Typography variant="h4" sx={{ color: color || 'var(--ac1, #7c4dff)', fontWeight: 'bold', mt: 1 }}>{value}</Typography>
    </CardContent>
  </Card>
);

export default function CostsPage() {
  const [sectOrder, setSectOrder] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem('costs_order')) || [0,1,2]; } catch { return [0,1,2]; }
  });
  const onDragEndSect = (from, to) => {
    const o = [...sectOrder]; const [m] = o.splice(from,1); o.splice(to,0,m);
    setSectOrder(o); localStorage.setItem('costs_order', JSON.stringify(o));
  };
  const [costs, setCosts] = useState(null);
  const [finance, setFinance] = useState(null);
  const [stats, setStats] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [openaiData, setOpenaiData] = useState(null);
  const [balanceInput, setBalanceInput] = useState('');
  const [balanceSaved, setBalanceSaved] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showClaudeEdit, setShowClaudeEdit] = useState(false);
  const [claudeBalanceInput, setClaudeBalanceInput] = useState('');
  const [claudeBalanceSaved, setClaudeBalanceSaved] = useState(false);
  const [claudeData, setClaudeData] = useState(null);

  const saveClaudeBalance = async () => {
    try {
      const current = claudeData?.balance_usd || 0;
      const adjustment = parseFloat(claudeBalanceInput);
      const newBalance = current + adjustment;
      await saveAnthropicBalance(newBalance);
      setClaudeBalanceSaved(true);
      setShowClaudeEdit(false);
      setClaudeData({ balance_usd: newBalance, remaining_usd: newBalance });
      setTimeout(() => setClaudeBalanceSaved(false), 2000);
      setClaudeBalanceInput('');
    } catch(e) { console.error('Claude save error:', e); }
  };

  const saveBalance = async () => {
    try {
      const current = openaiData?.manual_balance_usd || 0;
      const adjustment = parseFloat(balanceInput);
      const newBalance = current + adjustment;
      await saveOpenAIBalance(newBalance);
      setBalanceSaved(true);
      setShowEdit(false);
      setTimeout(() => setBalanceSaved(false), 2000);
      getOpenAIBilling().then(r => setOpenaiData(r.data));
    } catch(e) {}
  };

  useEffect(() => {
    getOpenAIBilling().then(r => setOpenaiData(r.data)).catch(() => {});
    getAnthropicBilling().then(r => setClaudeData(r.data)).catch(() => {});
    Promise.all([getCostsSummary(dateFrom||null, dateTo||null), getFinance(), getStats()])
      .then(([c, f, s]) => {
        setCosts(c.data);
        setFinance(f.data);
        setStats(s.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress sx={{ color: 'var(--ac1, #7c4dff)' }}/></Box>;

  const dailyCosts = (costs?.daily || []).map(d => ({
    date: d.date ? d.date.substring(5) : '',
    cost: parseFloat(d.cost || 0).toFixed(4),
    videos: d.videos
  }));

  const monthlyRevenue = (finance?.monthly || []).map(m => ({
    month: m.month,
    revenue: parseFloat(m.total || 0),
  })).reverse();

  const monthlyCosts = (costs?.monthly || []).map(m => ({
    month: m.month,
    cost: parseFloat(m.cost || 0).toFixed(4),
    videos: m.videos
  })).reverse();

  const applyFilter = () => {
    console.log("Filter:", dateFrom, dateTo);
    Promise.all([getCostsSummary(dateFrom||null, dateTo||null), getFinance(), getStats()])
      .then(([c,f,s]) => { console.log("Result:", c.data); setCosts(c.data); setFinance(f.data); setStats(s.data); })
      .catch(e => console.error("Error:", e));
  };
  const resetFilter = () => { setDateFrom(''); setDateTo(''); 
    Promise.all([getCostsSummary(null, null), getFinance(), getStats()])
      .then(([c,f,s]) => { setCosts(c.data); setFinance(f.data); setStats(s.data); });
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ color: '#fff', mb: 2, fontWeight: 'bold' }}>💹 Витрати та Дохід</Typography>
      <Box sx={{ display:'flex', gap:1, mb:3, flexWrap:'wrap', alignItems:'center' }}>
        <TextField size="small" type="date" label="Від" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          InputLabelProps={{shrink:true}} sx={{ input:{color:'#fff'}, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'}, label:{color:'#aaa'} }}/>
        <TextField size="small" type="date" label="До" value={dateTo} onChange={e => setDateTo(e.target.value)}
          InputLabelProps={{shrink:true}} sx={{ input:{color:'#fff'}, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'}, label:{color:'#aaa'} }}/>
        <Button variant="contained" onClick={applyFilter} sx={{ bgcolor:'var(--ac1, #7c4dff)' }}>🔍 Застосувати</Button>
        <Button variant="outlined" onClick={resetFilter} sx={{ borderColor:'#555', color:'#aaa' }}>Скинути</Button>
      </Box>

      {openaiData && (
        <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid #10a37f', p: 2, mb: 3 }}>
          <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:2, flexWrap:'wrap', gap:1 }}>
            <Typography variant="subtitle1" sx={{ color: '#10a37f' }}>🤖 OpenAI Рахунок</Typography>
            <Box sx={{ display:'flex', gap:1, alignItems:'center' }}>
              <Button size="small" onClick={() => setShowEdit(!showEdit)} sx={{ color:'#10a37f', minWidth:30 }}>✏️</Button>
              {showEdit && <>
                <TextField size="small" placeholder="+ додати / - відняти" type="number"
                  value={balanceInput} onChange={e => setBalanceInput(e.target.value)}
                  sx={{ width:100, input:{color:'#fff'}, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}/>
                <Button size="small" variant="contained" onClick={saveBalance} sx={{ bgcolor:'#10a37f' }}>💾</Button>
              </>}
              <Button size="small" variant="outlined" onClick={async () => { await saveOpenAIBalance(0); getOpenAIBilling().then(r => setOpenaiData(r.data)); setBalanceSaved(true); setTimeout(()=>setBalanceSaved(false),2000); }} sx={{ borderColor:'#f44336', color:'#f44336', minWidth:30 }}>🔄</Button>
              {balanceSaved && <Typography variant="caption" sx={{ color:'#4caf50' }}>✅</Typography>}
            </Box>
          </Box>
          <Grid container spacing={2}>
            {[
              { l: '💵 Баланс', v: '$' + (openaiData.manual_balance_usd || 0).toFixed(2), c: '#4caf50' },
              { l: '💰 Залишок', v: '$' + (openaiData.remaining_usd || 0).toFixed(4), c: (openaiData.remaining_usd||0) >= 0 ? '#4caf50' : '#f44336' },
              { l: '🎬 Відео оброблено', v: (openaiData.db_total_videos || 0) + ' (' + (stats?.total_versions||0) + ' верс.)', c: '#ff9800' },
              { l: '📊 Середня вартість/відео', v: '$' + (openaiData.db_avg_cost_usd || 0) + ' / €' + (openaiData.db_avg_cost_eur || 0), c: '#aaa' },
              { l: '💰 Витрати AI сьогодні', v: '$' + (openaiData.db_today_cost_usd || 0), c: '#f44336' },
              { l: '🎬 Відео сьогодні', v: (openaiData.db_today_videos || 0) + ' (' + (stats?.versions_today||0) + ' верс.)', c: '#4caf50' },
            ].map(item => (
              <Grid item xs={6} md={3} key={item.l}>
                <Box sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>{item.l}</Typography>
                  <Typography variant="body2" sx={{ color: item.c, fontWeight: 'bold' }}>{item.v}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Всього витрат AI" value={'$' + (costs?.total || 0).toFixed(4)} color="#f44336" icon="🤖"/>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Дохід всього" value={(finance?.total || 0).toFixed(0) + ' EUR'} color="#4caf50" icon="💰"/>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Відео сьогодні" value={(stats?.videos_today || 0) + ' (' + (stats?.versions_today||0) + ' верс.)'} color="#2196f3" icon="🎬"/>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Всього юзерів" value={stats?.total_users || 0} color="var(--ac1, #7c4dff)" icon="👥"/>
        </Grid>
      </Grid>

      <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid var(--ac1, #7c4dff)', p: 2, mb: 3 }}>
        <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:2, flexWrap:'wrap', gap:1 }}>
          <Typography variant="subtitle1" sx={{ color: 'var(--ac1, #7c4dff)' }}>🧠 Anthropic (Claude)</Typography>
          <Box sx={{ display:'flex', gap:1, alignItems:'center' }}>
            <Button size="small" onClick={() => setShowClaudeEdit(!showClaudeEdit)} sx={{ color:'var(--ac1, #7c4dff)', minWidth:30 }}>✏️</Button>
              {showClaudeEdit && <>
              <TextField size="small" placeholder="+ додати / - відняти" type="number"
                value={claudeBalanceInput} onChange={e => setClaudeBalanceInput(e.target.value)}
                sx={{ width:100, input:{color:'#fff'}, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#555'} }}/>
              <Button size="small" variant="contained" onClick={saveClaudeBalance} sx={{ bgcolor:'var(--ac1, #7c4dff)' }}>💾</Button>
              </>}
              <Button size="small" variant="outlined" onClick={async () => { await saveAnthropicBalance(0); setClaudeData({ balance_usd: 0, remaining_usd: 0 }); setClaudeBalanceSaved(true); setTimeout(()=>setClaudeBalanceSaved(false),2000); }} sx={{ borderColor:'#f44336', color:'#f44336', minWidth:30 }}>🔄</Button>
            {claudeBalanceSaved && <Typography variant="caption" sx={{ color:'#4caf50' }}>✅</Typography>}
          </Box>
        </Box>
        <Grid container spacing={2}>
          {[
            { l: '💵 Баланс', v: '$' + (claudeData?.balance_usd || 0).toFixed(2), c: '#4caf50' },
            { l: '💰 Залишок', v: '$' + (claudeData?.remaining_usd || 0).toFixed(4), c: (claudeData?.remaining_usd||0) >= 0 ? '#4caf50' : '#f44336' },
          ].map(item => (
            <Grid item xs={6} md={3} key={item.l}>
              <Box sx={{ p:1, bgcolor:'rgba(255,255,255,0.03)', borderRadius:1 }}>
                <Typography variant="caption" sx={{ color:'#888', display:'block' }}>{item.l}</Typography>
                <Typography variant="body2" sx={{ color:item.c, fontWeight:'bold' }}>{item.v}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid #333', p: 2 }}>
            <Typography variant="subtitle1" sx={{ color: '#f44336', mb: 2 }}>🤖 Витрати AI (30 днів)</Typography>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={dailyCosts}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333"/>
                <XAxis dataKey="date" stroke="#aaa" fontSize={10}/>
                <YAxis stroke="#aaa" fontSize={10}/>
                <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #333', color: '#fff' }}
                  formatter={(v) => ['$' + parseFloat(v).toFixed(4), 'Витрати']}/>
                <Bar dataKey="cost" fill="#f44336" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid #333', p: 2 }}>
            <Typography variant="subtitle1" sx={{ color: '#4caf50', mb: 2 }}>💰 Дохід по місяцях</Typography>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthlyRevenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333"/>
                <XAxis dataKey="month" stroke="#aaa" fontSize={10}/>
                <YAxis stroke="#aaa" fontSize={10}/>
                <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #333', color: '#fff' }}
                  formatter={(v) => [v.toFixed(0) + ' EUR', 'Дохід']}/>
                <Bar dataKey="revenue" fill="#4caf50" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid #333', p: 2 }}>
        <Typography variant="subtitle1" sx={{ color: 'var(--ac1, #7c4dff)', mb: 2 }}>📊 Витрати AI по місяцях</Typography>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={monthlyCosts}>
            <CartesianGrid strokeDasharray="3 3" stroke="#333"/>
            <XAxis dataKey="month" stroke="#aaa" fontSize={10}/>
            <YAxis stroke="#aaa" fontSize={10}/>
            <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #333', color: '#fff' }}
              formatter={(v) => ['$' + parseFloat(v).toFixed(4), 'Витрати']}/>
            <Line type="monotone" dataKey="cost" stroke="#7c4dff" strokeWidth={2} dot={{ fill: '#7c4dff' }}/>
          </LineChart>
        </ResponsiveContainer>
      </Paper>
    </Box>
  );
}
